import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, Sparkles, Lock, Tag } from "lucide-react";
import { toast } from "sonner";
import { api, local, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Loader } from "@/components/Loader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { money } from "@/lib/format";

export default function Subscription() {
  const { org, paywall, reload } = useAuth();
  const qc = useQueryClient();
  const cur = org?.currency || "NGN";
  const [promo, setPromo] = useState("");
  const [params, setParams] = useSearchParams();

  const { data, isLoading } = useQuery({
    queryKey: ["subscription"],
    queryFn: async () => (await api.get("/subscription")).data,
  });

  const plans = data?.plans || [];
  const priceOf = (code) => plans.find((p) => p.code === code)?.price || 0;

  const recordRedeem = (plan) => {
    if (!promo.trim()) return;
    local.post("/promo/redeem", { promo_code: promo.trim().toUpperCase(), plan, plan_price: priceOf(plan), currency: cur }).catch(() => {});
  };

  const changeMut = useMutation({
    mutationFn: async (plan) => (await api.post("/subscription", { plan, promo_code: promo.trim() || undefined })).data,
    onSuccess: (_, plan) => { recordRedeem(plan); toast.success(`Switched to ${plan} plan`); qc.invalidateQueries({ queryKey: ["subscription"] }); reload(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const checkoutMut = useMutation({
    mutationFn: async (plan) => (await local.post("/pay/subscription/checkout", { plan, origin_url: `${window.location.origin}/app/subscription` })).data,
    onSuccess: (d, plan) => {
      if (d?.url) { sessionStorage.setItem("fhapas_pending_plan", plan); sessionStorage.setItem("fhapas_pending_promo", promo.trim()); window.location.href = d.url; }
    },
    onError: (e) => toast.error(errMsg(e, "Could not start checkout. Please try again.")),
  });

  // Stripe return handler: activate the plan on paid, or show a clear retry prompt on cancel/decline.
  useEffect(() => {
    const cancelled = params.get("payment") === "cancelled";
    const sid = params.get("session_id");
    const pendingPlan = sessionStorage.getItem("fhapas_pending_plan") || "";
    const retry = pendingPlan ? { label: "Try again", onClick: () => checkoutMut.mutate(pendingPlan) } : undefined;
    if (cancelled) {
      setParams({}, { replace: true });
      toast.error("Payment was not completed", { description: "Your plan is unchanged.", action: retry });
      return;
    }
    if (!sid) return;
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const s = (await local.get(`/pay/status/${sid}`)).data;
        const paid = s?.payment_status === "paid" || s?.status === "completed";
        if (paid) {
          const plan = pendingPlan || s?.plan || "";
          const pc = sessionStorage.getItem("fhapas_pending_promo") || "";
          if (plan) { try { await api.post("/subscription", { plan, promo_code: pc || undefined }); } catch (_) {} }
          if (pc) local.post("/promo/redeem", { promo_code: pc.toUpperCase(), plan, plan_price: priceOf(plan), currency: cur }).catch(() => {});
          sessionStorage.removeItem("fhapas_pending_plan"); sessionStorage.removeItem("fhapas_pending_promo");
          toast.success("Payment successful — your plan is now active", {
            description: "A PDF receipt has been emailed to you.",
            duration: 15000,
            action: s?.receipt_url ? { label: "Receipt", onClick: () => window.open(`${process.env.REACT_APP_BACKEND_URL}${s.receipt_url}`, "_blank") } : undefined,
          });
          await reload(); qc.invalidateQueries({ queryKey: ["subscription"] });
          setParams({}, { replace: true });
          return;
        }
        if (["failed", "expired"].includes(s?.payment_status) || s?.status === "failed") {
          setParams({}, { replace: true });
          toast.error("Payment failed", { description: "Your card was declined. Your plan is unchanged.", action: retry });
          return;
        }
      } catch (_) {}
      if (tries < 8) setTimeout(poll, 2000);
      else { setParams({}, { replace: true }); toast.error("We couldn't confirm your payment", { description: "If you were charged it will update shortly; otherwise your plan is unchanged.", action: retry }); }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  if (isLoading) return <Loader full />;

  const current = data?.current_plan;

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader title="Subscription" subtitle={`Current plan: ${current?.toUpperCase() || "TRIAL"}`} testId="page-subscription" />

      {paywall?.enabled && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="paywall-notice">
          <Lock className="mt-0.5 h-5 w-5 text-amber-600" />
          <div className="text-sm text-amber-800">
            <p className="font-semibold">Subscription enforcement is active for your organisation</p>
            <p>A paid plan ({(paywall.plan_required || "standard").toUpperCase()}+) is required to keep using clinical modules. Choose a plan below.</p>
          </div>
        </div>
      )}

      <div className="mb-6 flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-4 sm:max-w-md">
        <Label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><Tag className="h-3.5 w-3.5" /> Promo code</Label>
        <div className="flex items-center gap-2">
          <Input data-testid="promo-code-input" value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())} placeholder="e.g. LAUNCH50" />
          {promo && <span className="whitespace-nowrap text-xs font-medium text-emerald-600">Applied at checkout</span>}
        </div>
      </div>

      <div className="grid gap-5 md:grid-cols-3">
        {plans.map((p) => {
          const active = current === p.code;
          const paid = p.price > 0;
          return (
            <div key={p.code} data-testid={`plan-${p.code}`}
              className={`relative flex flex-col rounded-2xl border p-6 shadow-sm transition-all ${active ? "border-teal-600 ring-2 ring-teal-200" : "border-slate-200 hover:shadow-md"}`}>
              {p.code === "standard" && <span className="absolute -top-2.5 right-5 rounded-full bg-teal-700 px-2.5 py-0.5 text-[11px] font-bold text-white">POPULAR</span>}
              <h3 className="font-display text-lg font-bold text-slate-900">{p.name}</h3>
              <div className="mt-2 font-display text-3xl font-extrabold text-slate-900">
                {p.price === 0 ? "Free" : money(p.price, cur)}
                {p.price > 0 && <span className="text-sm font-medium text-slate-400">/mo</span>}
              </div>
              <ul className="mt-4 flex-1 space-y-2 text-sm text-slate-600">
                {(p.features || []).map((f) => <li key={f} className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-600" /> {f}</li>)}
              </ul>
              <div className="mt-6">
                {active ? (
                  <Button disabled className="w-full" variant="outline" data-testid={`current-${p.code}`}><Check className="mr-1.5 h-4 w-4" /> Current plan</Button>
                ) : paid ? (
                  <Button data-testid={`upgrade-${p.code}`} className="w-full gap-1.5 bg-teal-700 hover:bg-teal-800" onClick={() => checkoutMut.mutate(p.code)} disabled={checkoutMut.isPending}>
                    <Sparkles className="h-4 w-4" /> Upgrade
                  </Button>
                ) : (
                  <Button data-testid={`switch-${p.code}`} variant="outline" className="w-full" onClick={() => changeMut.mutate(p.code)}>Switch</Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-4 text-center text-xs text-slate-400">Billing is processed securely. Live card payments activate once the app is deployed with your payment key.</p>
    </div>
  );
}
